import React from 'react';
import { mapStateToProps, TopActivitiesAroundYouProps } from 'component/page/home/TopActivitiesAroundYou/TopActivitiesAroundYou.selectors';
import { Box, Carousel, HorizontalScroll, Typography } from '@drone-js/core';
import { connect } from 'react-redux';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { ActivityGqlOperation, OperationKey, SearchRequestType } from 'store/api/api.types';
import { activityCardMapper } from 'component/common/ActivityCard/ActivityCard.selectors';
import ActivityCard from 'component/common/ActivityCard/ActivityCard';
import { ActivitySingleCardPlaceHolder } from 'component/common/ActivityCard/PlaceHolder/ActivityCardPlaceHolder';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesTopActivitiesAroundYouCarouselArrowButtonClickEventActivitiesFieldsV1,
    ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesTopActivitiesAroundYouCarouselArrowButtonClickEventActivitiesManifestV1,
    ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ScrollDirection } from 'core/utils/useScrollDirection';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import RecommendedActivitiesCarousel from 'component/common/RecommendedActivitiesCarousel/RecommendedActivitiesCarousel';
import { SearchType } from 'gql/autogen/SearchType';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

const NUMBER_OF_ACTIVITIES = 12;

const TopActivitiesAroundYouPlaceholder: React.FC = () => {
    return (
        <HorizontalScroll pt="16" data-element-name="top-activities-around-you-carousel-placeholder">
            {[...Array<number>(NUMBER_OF_ACTIVITIES)].map((_, i) => (
                <Box key={i} me="12" width="270px">
                    <ActivitySingleCardPlaceHolder responsive={false} />
                </Box>
            ))}
        </HorizontalScroll>
    );
};
TopActivitiesAroundYouPlaceholder.displayName = 'TopActivitiesAroundYouPlaceholder';

const TopActivitiesAroundYouComponent: React.FC<TopActivitiesAroundYouProps> = (props) => {
    const [isLoading, setIsLoading] = React.useState(true); // Please remove once integrate HomePageHorizontalCarousels
    const [activitiesList, setActivitiesList] = React.useState<ActivityCardProps[]>([]); // Please remove once integrate HomePageHorizontalCarousels

    // Please remove once integrate HomePageHorizontalCarousels
    React.useEffect(() => {
        // The 'searchResultNumber' will change only when resizing the browser. DO not reload the page when user is resizing the browser for better UX.
        if (!props.isHomePageHorizontalCarousels) {
            queryActivitiesGqlApi({
                args: {
                    props: {
                        searchType: SearchType.CITY,
                        searchRequestType: SearchRequestType.POPULAR_ACTIVITIES,
                        pagination: { number: 1, size: NUMBER_OF_ACTIVITIES },
                        cityId: props.cityId,
                        defaultSort: { code: SortCode.ActivitiesHomepageTopActivitiesCarousel, order: SortOrder.Descending },
                    },
                    customStateHandling: true,
                },
                operation: ActivityGqlOperation.search,
                key: OperationKey.HomePageTopActivitiesAroundYou,
                onSuccess: (_operation, _args, response) => {
                    setIsLoading(false);
                    const list = activityCardMapper(response?.result?.activities || [], props.config);
                    setActivitiesList(list);
                },
                onError: () => {
                    setIsLoading(false);
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Please remove once integrate HomePageHorizontalCarousels
    const trackScrollEvent = React.useCallback((scrollDirection: ScrollDirection) => {
        trackElementEvent<ActivitiesTopActivitiesAroundYouCarouselArrowButtonClickEventActivitiesFieldsV1>(
            ActivitiesTopActivitiesAroundYouCarouselArrowButtonClickEventActivitiesManifestV1.ids,
            {
                action_element_value: scrollDirection,
            },
        );
    }, []);

    // Please remove once integrate HomePageHorizontalCarousels
    React.useEffect(() => {
        if (!props.isHomePageHorizontalCarousels && !isLoading && activitiesList.length > 0) {
            trackElementEvent<ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesFieldsV1>(ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesManifestV1.ids, {});
        }
    }, [isLoading, activitiesList, props.isHomePageHorizontalCarousels]);

    // Please remove once integrate HomePageHorizontalCarousels
    const onScroll = React.useCallback(
        (_index: number, _length: number, _scrollLeft: number, scrollDirection: 'next' | 'previous') => {
            trackScrollEvent(scrollDirection as ScrollDirection);
        },
        [trackScrollEvent],
    );

    // Please update to "if (!props.cityId) return null" once integrate HomePageHorizontalCarousels
    if (!props.cityId || (!props.isHomePageHorizontalCarousels && !isLoading && activitiesList.length === 0)) {
        return null;
    }

    return (
        <Box display="flex" flexDirection="column" width="100%" mb={props.isPageContainerSpacing ? '32' : '48'} data-testid="top-activities-around-you-carousel-container">
            <Feature.Off name={FeatureName.HomePageHorizontalCarousels}>
                <Typography variant="heading-default">{props.config.cms[321405]}</Typography>
                <>
                    {isLoading ? (
                        <TopActivitiesAroundYouPlaceholder />
                    ) : (
                        <Carousel
                            currentCard={0}
                            accessibilityNextNavLabel={props.config.cms[130706]}
                            accessibilityPreviousNavLabel={props.config.cms[130765]}
                            onScroll={onScroll}
                            pt="16"
                            px="4"
                            cardSpacing="2"
                            data-element-name="top-activities-around-you-carousel"
                            data-element-value="activities-top-activities-carousel"
                        >
                            {activitiesList.map((activity, index) => (
                                <div key={index}>
                                    <ActivityCard
                                        index={index}
                                        activity={activity}
                                        pageTypeId={props.pageTypeId}
                                        isPopularActivitiesCard={true}
                                        isSearchBoxFeatureGroup={props.isSearchBoxFeatureGroup}
                                        elementName="activities-top-activities-carousel"
                                        shouldShowCityName={false}
                                    />
                                </div>
                            ))}
                        </Carousel>
                    )}
                </>
            </Feature.Off>
            <Feature.On name={FeatureName.HomePageHorizontalCarousels}>
                <RecommendedActivitiesCarousel
                    cityId={props.cityId}
                    title={props.config.cms[321405]}
                    isPopularActivities={true}
                    operationKey={OperationKey.HomePageTopActivitiesAroundYou}
                    dataElementName="top-activities-around-you-carousel"
                    dataElementValue="activities-top-activities-horizontal-scroll"
                    numberOfActivities={NUMBER_OF_ACTIVITIES}
                    defaultSort={{
                        code: SortCode.ActivitiesHomepageTopActivitiesCarousel,
                        order: SortOrder.Descending,
                    }}
                />
            </Feature.On>
        </Box>
    );
};

TopActivitiesAroundYouComponent.displayName = 'TopActivitiesAroundYou';

export default connect(mapStateToProps)(TopActivitiesAroundYouComponent);
export { TopActivitiesAroundYouPlaceholder };
